import type * as Action from './actions';
import {
  downloadIncidentsReportEnd,
  downloadRoutesPointsReportEnd,
} from './actionCreators';
import { ReportsActionTypes } from './reportsActionTypes';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import { PerformanceReports } from '@cobliteam/api-lib';

import { AlertTypes, showAlertActionCreator } from '@cobliteam/alert';
import { Analytics } from '@cobliteam/analytics';
import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const reportsRequester = new PerformanceReports({ host: env.herbieApiHost });

const downloadReportFile = (
  blob: Blob,
  name: string,
  startInMilli: number,
  endInMilli: number,
  format = 'xls'
): void => {
  const startTimeStr = moment(startInMilli).format('YYMMDD');
  const endTimeStr = moment(endInMilli).format('YYMMDD');
  const periodStr =
    startTimeStr === endTimeStr
      ? startTimeStr
      : `${startTimeStr}_${endTimeStr}`;
  const filename = `Cobli_${periodStr}_${name}.${format}`;
  saveAs(blob, filename);
};

export function* requestDownloadIncidentsReportSaga(
  action: Action.DownloadIncidentsReportRequest
) {
  const { start, end } = action.payload;

  try {
    const reportBlob: Blob = yield call(
      reportsRequester.getIncidentsReport,
      start,
      end
    );
    downloadReportFile(reportBlob, 'alertas', start, end);
  } catch (err) {
    yield put(
      showAlertActionCreator(
        AlertTypes.ERROR,
        'Ops! O sistema apresentou um erro e não foi possível exportar o relatório, tente novamente.'
      )
    );
  } finally {
    yield put(downloadIncidentsReportEnd());
  }
}

export function* requestDownloadRoutesPointsReportSaga(
  action: Action.DownloadRoutesPointsReportRequest
) {
  const { period, vehicleId, sampleInMinutes } = action.payload;
  const daysCount = moment(period.end).diff(moment(period.start), 'days');

  try {
    Analytics.track('Points Report Requested', { days: daysCount });
    const reportBlob: Blob = yield call(
      reportsRequester.getRoutesPointsReport,
      period,
      vehicleId,
      sampleInMinutes
    );
    downloadReportFile(reportBlob, 'pontostrajetos', period.start, period.end);
    Analytics.track('Points Report Exported', { days: daysCount });
  } catch (err) {
    Analytics.track('Points Report Error', { days: daysCount });
    yield put(
      showAlertActionCreator(
        AlertTypes.ERROR,
        'Ops! O sistema apresentou um erro e não foi possível exportar o relatório, tente novamente.'
      )
    );
  } finally {
    yield put(downloadRoutesPointsReportEnd());
  }
}

export function* ReportsSagasListener() {
  yield all([
    takeLatest(
      ReportsActionTypes.DOWNLOAD_INCIDENTS_REPORT_REQUEST,
      requestDownloadIncidentsReportSaga
    ),
    takeLatest(
      ReportsActionTypes.DOWNLOAD_ROUTES_POINTS_REPORT_REQUEST,
      requestDownloadRoutesPointsReportSaga
    ),
  ]);
}
